<template>
  <ValidationObserver>
    <b-modal
      id="editTodoListConfig"
      v-model="modal.show"
      :modal-class="modalClass"
      title="Adjust ToDo List Configurations"
      :ok-disabled="isDisabled"
      :ok-title="`Save`"
      footer-class="editTodoListFooter"
      @ok="onSaveClick"
      @show="onShowModal"
      @hide="onHideModal"
    >
      <content-spinner :show="loading" />
      <div v-show="!loading">
        <div>
          <b-tabs
            id="ToDoListConfigOptions"
            v-model="toDoListConfig.selectedConfigOption"
            class="custom-control"
            fill
            content-class="mt-3"
          >
            <b-tab
              title="Users"
              active
              class="ToDoListConfigOption"
            >
              <div class="d-flex">
                <div class="col-6">
                  <users-list
                    id="todoListUsersList"
                    v-model="toDoListConfig.selectedUsers"
                    :show-selection="false"
                    @userSelect="(user) => onUserClick(user, 'select')"
                    @userRemove="(user) => onUserClick(user, 'remove')"
                  />
                </div>
                <div
                  v-if="selectedUsers.length > 0"
                  class="col-6"
                >
                  <users-list
                    id="todoListSelectedUsersList"
                    :users-props="selectedUsers"
                    :fields="table.fields.filter(f => !f.isNew)"
                    :show-selection="false"
                    :show-alert="false"
                    :show-configurable="true"
                    @onFieldSelected="onFieldSelected"
                    @onCheckboxChanged="onCheckboxChange"
                    @onUserRemove="onUserRemove"
                  />
                </div>
              </div>
            </b-tab>

            <b-tab
              title="Custom SQL"
              class="ToDoListConfigOption"
            >
              <div>
                <b-form-checkbox
                  :id="`todoListSendEmailAlerts`"
                  v-model="toDoListConfig.sendEmailAlert"
                  class="mt-3"
                  :value="true"
                  :unchecked-value="false"
                  :checked="toDoListConfig.sendEmailAlert"
                >
                  Send Email Alerts
                </b-form-checkbox>
                <b-textarea
                  id="todoListCustomSQLTextarea"
                  v-model="toDoListConfig.customSQL"
                  class="mt-2"
                  rows="10"
                  placeholder="Custom SQL"
                />
              </div>
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </b-modal>
  </ValidationObserver>
</template>
<script>
import UsersList from '@/modules/DataManagement/components/UsersList.vue';
import InputWidget from '@/core/components/complex-table/components/InputWidget.vue';
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '@/core/components/ContentSpinner.vue';
import {hideTodoListModal} from '@/store/modules/data-management/actions';
import {SET_TABLE_SCHEMA} from '@/store/modules/data-management/mutation-types';

const {mapState, mapActions, mapGetters, mapMutations} = createNamespacedHelpers('dataManagement');

export default {
  name: 'ToDoListModal',
  components: {
    ContentSpinner,
    InputWidget,
    UsersList,
  },
  props: {
    table: {
      type: Object,
      require: true,
    },
    fields: {
      type: Object,
      require: true,
    },
  },
  data() {
    return {
      toDoListConfig: {
        selectedConfigOption: 0,
        selectedField: null,
        sendEmailAlert: false,
        selectedUsers: [],
        customSQL: '',
      },
      selectedUsers: [],
    };
  },
  computed: {
    ...mapState({
      schema: state => state.builder.schema,
      loading: state => state.loading,
      modal: state => state.todoListModal,
    }),
    modalClass() {
      return this.toDoListConfig.selectedConfigOption === 0
        ? 'todolist-modal wide-modal'
        : 'todolist-modal';
    },
    isDisabled() {
      return this.toDoListConfig.selectedConfigOption === 0
        ? !this.selectedUsers.every(u => u.FieldToUseAsRecordNameGuid)
        : false;
    },
  },
  methods: {
    ...mapActions(['hideTodoListModal', 'updateTodoListConfig']),
    ...mapMutations({
      setTableSchema: SET_TABLE_SCHEMA,
    }),
    onUserClick(user, action) {
      switch (action) {
      case 'select':
        const newUser = {
          UserGuid: user.id,
          FieldToUseAsRecordNameGuid: null,
          SendEmailAlert: null,
        };
        this.selectedUsers.push(newUser);
        this.toDoListConfig.selectedField = null;
        this.toDoListConfig.sendEmailAlert = false;
        break;
      case 'remove':
        this.selectedUsers = this.selectedUsers.filter(u => u.UserGuid !== user.id);
        break;
      default:
        break;
      }
    },
    async onSaveClick() {
      const toDoListConfigData = {
        UsersToNotifyForRecordStatus: this.selectedUsers,
        CustomSQL: this.toDoListConfig.customSQL,
        CustomSQLSendAlerts: this.toDoListConfig.sendEmailAlert,
      };

      this.updateTodoListConfig(toDoListConfigData);
    },
    onShowModal() {
      if(!this.schema.toDoListConfig) return;
      const todoListConfigJson = this.schema.toDoListConfig || {};

      this.toDoListConfig.customSQL = todoListConfigJson.CustomSQL;
      this.toDoListConfig.sendEmailAlert = todoListConfigJson.CustomSQLSendAlerts;
      this.selectedUsers = [...todoListConfigJson.UsersToNotifyForRecordStatus];
      this.selectedUsers.forEach(user => {
        this.toDoListConfig.selectedUsers.push(user.UserGuid);
      });
    },
    onHideModal() {
      this.hideTodoListModal();
      this.toDoListConfig = {
        selectedUsers: [],
      };
      this.selectedUsers = [];
    },
    onFieldSelected(data) {
      const [value, user] = data;
      this.selectedUsers = this.selectedUsers.map(u => {
        if (u.UserGuid === user.id) {
          return {...u, FieldToUseAsRecordNameGuid: value};
        }
        return u;
      });
    },
    onCheckboxChange(data) {
      const [value, user] = data;
      this.selectedUsers = [...this.selectedUsers.map(u => {
        if (u.UserGuid === user.id) {
          return {...u, SendEmailAlert: value};
        }
        return u;
      })];

    },
    onUserRemove(userId) {
      this.selectedUsers = this.selectedUsers.filter(u => u.UserGuid !== userId);
      this.toDoListConfig.selectedUsers = this.toDoListConfig.selectedUsers.filter(uId => uId !== userId);
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep .nav-tabs {
  .nav-link {

    &.active {
      background-color: transparent;
      border-bottom-color: #FFFFFF;
    }
  }
}
</style>
<style lang="scss">
.todolist-modal.wide-modal .modal-dialog {
  max-width: 900px;
}
</style>